import { h, Component, createRef } from "preact";
import { cropImage, getResizedImageSources } from "../foundation/utils/image";
import CSSProperties from "../foundation/model/CSSProperties";

interface Props {
    source: string;
    class?: string;
    width?: number;
    height?: number;
    style?: CSSProperties;
    onLoad?: () => void;
    onClick?: (ev: MouseEvent) => void;
}

interface State {
    src: string | null;
    srcset: string | null;
}

class ResizedImage extends Component<Props, State> {
    override state = {
        src: null,
        srcset: null
    };

    imgRef = createRef();

    get dimensions() {
        const { width, height } = this.props;
        if (typeof width !== "undefined" && typeof height !== "undefined") {
            return {
                width,
                height
            };
        }

        if (!this.imgRef.current) {
            return null;
        }

        const { width: elWidth, height: elHeight } = this.imgRef.current;

        return {
            width: elWidth,
            height: elHeight
        };
    }

    load(source: string) {
        if (!this.dimensions) {
            return;
        }

        const { src, srcset } = getResizedImageSources(source, this.dimensions);

        this.setState({
            src,
            srcset
        });
    }

    handleError = () => {
        const { source } = this.props;

        if (!source) {
            return null;
        }

        if (!this.dimensions) {
            return;
        }

        const { width, height } = this.dimensions;

        Promise.all([
            cropImage(source, {
                width,
                height
            }),
            cropImage(source, {
                width: width * 2,
                height: height * 2
            })
        ]).then(([src, srcset]) => {
            this.setState({
                src,
                srcset
            });
        }).catch(() => {
            // No-op
        });
    };

    override componentDidMount() {
        const { source } = this.props;
        if (source) {
            this.load(source);
        }
    }

    override componentWillReceiveProps(nextProps: Props) {
        const { source: nextSource } = nextProps;
        const { source } = this.props;

        if (nextSource && nextSource !== source) {
            this.load(nextSource);
        }
    }

    render() {
        const { src, srcset } = this.state;
        const { source: _source, ...passProps } = this.props;

        return (
            <img
                {...passProps}
                src={src!}
                srcSet={srcset!}
                onError={this.handleError}
                ref={this.imgRef}/>
        );
    }
}

export default ResizedImage;
